import * as React from 'react'
import { useEffect } from 'react'
import Box from '@mui/material/Box'
import { useData, useHandlers } from './hooks'
import { useFetchActivityTypes } from '../../hooks'
import { ActivitiesListItem } from './ActivitiesListItem'
import { ActivitiesListHeader } from './ActivitiesListHeader'
import {
  useGetSalesTeamOnMount,
  useGetSalesTeamUsersOnMount,
} from '@user/store/salesTeam'
import { TabPanel } from '@sales-center/components'
import { TabsStyled } from './TabsStyled'
import { TabStyled } from './TabStyled'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'

type Props = {
  salesTeamId: number
  entityId: number
}

export function ActivitiesList(props: Props) {
  const { salesTeamId, entityId } = props
  const {
    activeActivities,
    deferredActivities,
    completedActivities,
    showList,
  } = useData()

  const state = useFetchActivityTypes()
  useGetSalesTeamUsersOnMount(salesTeamId)
  useGetSalesTeamOnMount(salesTeamId)
  const { tabIndex, handleTabChange, setColor } = useHandlers()

  useEffect(() => {
    // Tabs: 0 - active, 1 - deferred, 2 - completed. We check which tab has activities and set it as active
    const activeTab = [
      activeActivities,
      deferredActivities,
      completedActivities,
    ].findIndex((tab) => !!tab.length)
    handleTabChange(null, Math.max(activeTab, 0))
  }, [
    activeActivities,
    completedActivities,
    deferredActivities,
    handleTabChange,
  ])

  return (
    <Paper elevation={0} variant="outlined">
      <Stack width={353}>
        <ActivitiesListHeader state={state} entityId={entityId} />
        {showList && (
          <Stack>
            <TabsStyled
              indicatorColor="primary"
              onChange={handleTabChange}
              textColor="primary"
              value={tabIndex}
            >
              <TabStyled
                label={`Active (${activeActivities.length})`}
                color={setColor(0, activeActivities.length)}
                disabled={!activeActivities.length}
                data-test-id={`activity-active-tab-button-${entityId}`}
              />
              <TabStyled
                label={`Deferred (${deferredActivities.length})`}
                color={setColor(1, deferredActivities.length)}
                disabled={!deferredActivities.length}
                data-test-id={`activity-deferred-tab-button-${entityId}`}
              />
              <TabStyled
                label={`Completed (${completedActivities.length})`}
                color={setColor(2, completedActivities.length)}
                disabled={!completedActivities.length}
                data-test-id={`activity-completed-tab-button-${entityId}`}
              />
            </TabsStyled>

            <TabPanel value={0} tab={tabIndex}>
              <Box maxHeight={330} overflow="auto">
                <List>
                  {activeActivities.map((activity, index) => (
                    <ListItem
                      divider={index !== activeActivities.length - 1}
                      key={`activity+${index}`}
                    >
                      <ActivitiesListItem
                        id={activity.id}
                        activity={activity}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </TabPanel>
            <TabPanel value={1} tab={tabIndex}>
              <Box maxHeight={330} overflow="auto">
                <List>
                  {deferredActivities.map((activity, index) => (
                    <ListItem
                      divider={index !== deferredActivities.length - 1}
                      key={`activity+${index}`}
                    >
                      <ActivitiesListItem
                        id={activity.id}
                        activity={activity}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </TabPanel>
            <TabPanel value={2} tab={tabIndex}>
              <Box maxHeight={330} overflow="auto">
                <List>
                  {completedActivities.map((activity, index) => (
                    <ListItem
                      divider={index !== completedActivities.length - 1}
                      key={`activity+${index}`}
                    >
                      <ActivitiesListItem
                        id={activity.id}
                        activity={activity}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </TabPanel>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}
